import loglevel from 'loglevel';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { normalize } from '@sentry/utils';

class LoglevelSentry {
  constructor(sentry) {
    _defineProperty(this, "sentry", void 0);
    _defineProperty(this, "category", void 0);
    this.sentry = sentry;
    this.category = "loglevel-sentry";
  }
  static async translateError(args) {
    // Find first Error or create an "unknown" Error to keep stack trace.
    const index = args.findIndex(arg => arg instanceof Error);
    const msgIndex = args.findIndex(arg => typeof arg === "string");
    const apiErrorIdx = args.findIndex(arg => arg && typeof arg === "object" && "status" in arg && "type" in arg);
    const axiosErrorIdx = args.findIndex(arg => arg && typeof arg === "object" && "isAxiosError" in arg && arg.isAxiosError === true);
    let err;
    if (apiErrorIdx !== -1) {
      var _apiError$headers;
      const apiError = args[apiErrorIdx];
      const contentType = (_apiError$headers = apiError.headers) === null || _apiError$headers === void 0 ? void 0 : _apiError$headers.get("content-type");
      if (contentType.includes("application/json")) {
        const errJson = await apiError.json();
        err = new Error((errJson === null || errJson === void 0 ? void 0 : errJson.error) || (errJson === null || errJson === void 0 ? void 0 : errJson.message) || JSON.stringify(errJson));
      } else if (contentType.includes("text/plain")) {
        err = new Error(await apiError.text());
      } else {
        err = new Error(`${apiError.status} ${apiError.type.toString()} ${apiError.statusText}`);
      }
    } else if (axiosErrorIdx !== -1) {
      var _errorResponse$header;
      const axiosError = args[axiosErrorIdx];
      const errorResponse = axiosError.response;
      const contentType = (_errorResponse$header = errorResponse.headers) === null || _errorResponse$header === void 0 ? void 0 : _errorResponse$header["content-type"];
      if (contentType.includes("application/json")) {
        const errJson = errorResponse.data;
        const errorMsg = "error" in errJson ? errJson.error : "message" in errJson ? errJson.message : JSON.stringify(errJson);
        err = new Error(errorMsg);
      } else if (contentType.includes("text/plain")) {
        err = new Error(errorResponse.data);
      } else {
        err = new Error(`${errorResponse.status} ${errorResponse.data.toString()}`);
      }
    } else if (index !== -1) {
      err = args.splice(index, 1)[0];
    } else if (msgIndex !== -1) {
      err = new Error(args.splice(msgIndex, 1)[0]);
    } else {
      err = new Error("Unknown error");
    }
    return [err, args];
  }
  static translateArgs(args) {
    const msgIndex = args.findIndex(arg => typeof arg === "string");
    const firstMsg = msgIndex !== -1 ? args.splice(msgIndex, 1)[0] : undefined;
    return firstMsg ? {
      message: firstMsg,
      // args is already spliced
      data: {
        arguments: args
      }
    } : {
      data: {
        arguments: args
      }
    };
  }
  static translateLevel(level) {
    switch (level) {
      case "info":
        return "info";
      case "warn":
        return "warning";
      default:
        return "debug";
    }
  }
  install(logger) {
    var _this = this;
    const defaultMethodFactory = logger.methodFactory;
    logger.methodFactory = (method, level, name) => {
      if (name) this.category = name.toString();
      const defaultMethod = defaultMethodFactory(method, level, name);
      const isFrontend = typeof window !== "undefined";
      const overrideDefaultMethod = function () {
        var _this$sentry$getActiv;
        const {
          traceId,
          spanId
        } = ((_this$sentry$getActiv = _this.sentry.getActiveSpan()) === null || _this$sentry$getActiv === void 0 ? void 0 : _this$sentry$getActiv.spanContext()) || {};
        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }
        const isError = method === "error" && args.length >= 1 && args[0] instanceof Error;
        if (isFrontend) {
          if (isError) {
            const error = args[0];
            const newError = new Error(`${error.message}: traceId: ${traceId} - spanId: ${spanId}`, {
              cause: error
            });
            const newArgs = [newError, ...args.slice(1)];
            if (defaultMethod) defaultMethod(...newArgs);
            return;
          }
          if (defaultMethod) defaultMethod(...args);
        } else {
          let logData = {
            timestamp: new Date(),
            level: method.toUpperCase(),
            logger: name,
            traceId,
            spanId
          };
          if (isError) {
            var _error$message;
            const error = args[0];
            logData = _objectSpread(_objectSpread({}, logData), {}, {
              message: (_error$message = error.message) !== null && _error$message !== void 0 ? _error$message : "",
              stack: error.stack,
              extra: args.length > 1 ? args.slice(1) : undefined
            });
          } else {
            var _args$;
            logData = _objectSpread(_objectSpread({}, logData), {}, {
              message: (_args$ = args[0]) !== null && _args$ !== void 0 ? _args$ : "",
              extra: args.length > 1 ? args.slice(1) : undefined
            });
          }
          if (defaultMethod) defaultMethod(JSON.stringify(normalize(logData)));
        }
      };
      switch (method) {
        case "error":
          return async function () {
            for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
              args[_key2] = arguments[_key2];
            }
            const [err, otherArgs] = await LoglevelSentry.translateError(args);
            _this.error(err, ...otherArgs);
            overrideDefaultMethod(err, ...otherArgs);
          };
        default:
          return function () {
            for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
              args[_key3] = arguments[_key3];
            }
            _this.log(LoglevelSentry.translateLevel(method), ...args);
            // keep behavior consistent to console in browser
            overrideDefaultMethod(...args);
          };
      }
    };
    logger.setLevel(logger.getLevel());
  }
  setEnabled(enabled) {
    this.sentry.getClient().getOptions().enabled = enabled;
  }
  isEnabled() {
    return this.sentry.getClient().getOptions().enabled;
  }
  log(level) {
    for (var _len4 = arguments.length, args = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
      args[_key4 - 1] = arguments[_key4];
    }
    this.sentry.addBreadcrumb(_objectSpread(_objectSpread({}, LoglevelSentry.translateArgs(args)), {}, {
      category: this.category,
      level,
      timestamp: Date.now()
    }));
  }
  trace() {
    for (var _len5 = arguments.length, args = new Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
      args[_key5] = arguments[_key5];
    }
    this.log("debug", ...args);
  }
  error(err) {
    for (var _len6 = arguments.length, args = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
      args[_key6 - 1] = arguments[_key6];
    }
    this.sentry.captureException(err, {
      tags: {
        logger: "loglevel-sentry",
        "logger.name": this.category
      },
      extra: {
        arguments: args
      }
    });
  }
}

function createLogger(name, sentry) {
  const logger = loglevel.getLogger(name);
  logger.enableAll();
  const plugin = new LoglevelSentry(sentry);
  plugin.install(logger);
  return logger;
}

const defaultPattern = /(private|key|secret|authorization|address|email|login_hint)+/i;
function isHex(s) {
  return /^(-0x|0x)?[0-9a-f]*$/i.test(s);
}
function redactInternalEventData(data) {
  let keyPattern = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultPattern;
  if (typeof data !== "object" || data === null) return data;
  const keys = Object.keys(data);
  for (const k of keys) {
    const v = data[k];
    if (typeof v === "string" && keyPattern.test(k)) data[k] = "***";else data[k] = redactInternalEventData(v, keyPattern);
  }
  return data;
}
function redactEventData(data) {
  let keyPattern = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultPattern;
  let maxDepth = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 8;
  const normalizedData = normalize(data, maxDepth);
  return redactInternalEventData(normalizedData, keyPattern);
}
function redactInternalBreadcrumbData(data) {
  let result = data;
  if (typeof data === "object") {
    result = _objectSpread({}, data);
  } else if (Array.isArray(data)) {
    // Workaround TypeScript limitation to map types between `data` and `result`
    result = [...data];
  } else {
    return data;
  }
  for (const k in data) {
    if (Object.prototype.hasOwnProperty.call(data, k)) {
      const v = data[k];
      if (typeof v === "string" && isHex(v)) {
        // Workaround TypeScript limitation to map types between `data` and `result`
        result[k] = "***";
      } else {
        try {
          result[k] = redactInternalBreadcrumbData(v);
        } catch (error) {
          // We are not able to redact the value
          result[k] = "***";
        }
      }
    }
  }
  return result;
}
function redactBreadcrumbData(data) {
  let maxDepth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 8;
  if (!data) return data;
  const normalizedData = normalize(data, maxDepth);
  return redactInternalBreadcrumbData(normalizedData);
}

export { createLogger, LoglevelSentry as default, redactBreadcrumbData, redactEventData };
